<template>
  <ProgressCircular v-if="loading" />
  <form v-else novalidate :disabled="disabled" @submit.prevent="() => {}">
    <v-row no-gutters>
      <v-col cols="8">
        <v-card max-width="1200px" flat color="transparent">
          <v-card-title>
            <FormHeader
              :loading="loading"
              :disabled="disabled"
              :link="'page-tourist-agencies'"
              :title="
                getTitle
                  ? `${getTitle} туристическое агенство`
                  : `Создать туристическое агенство`
              "
            />
          </v-card-title>
          <v-card-text>
            <v-container>
              <PageContent height="85">
                <InputTitleSlug
                  :is-loading="loading || disabled"
                  :title="fields.name"
                  :title-error="validationErrors.name"
                  :slug="fields.slug"
                  :slug-error="validationErrors.slug"
                  :isSlugEnabled="!isNew"
                  @change:title="fields.name = $event"
                  @change:slug="fields.slug = $event"
                  @input:slug="$v.fields.slug.$touch"
                />
                <v-row>
                  <v-col cols="12" class="pb-0"
                    ><span>Описание* (<TranslationIcon />)</span></v-col
                  >
                  <QuillEditorVue
                    id="description"
                    :cols="12"
                    :title="''"
                    :content="fields.description"
                    :loading="loading"
                    :disabled="disabled"
                    :isShowErrorMessage="true"
                    :errorText="validationErrors.description"
                    @input="fields.description = $event"
                    @onChangeContent="$emit('change:descriptionForMobile', { fields, $event })"
                  />
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <span>Краткое описание* (<TranslationIcon />)</span>
                    <v-textarea
                      v-model="fields.shortDescription"
                      id="shortDescription"
                      outlined
                      :loading="loading"
                      :disabled="disabled"
                      :error-messages="validationErrors.shortDescription"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12">
                    <MediaFilePicker
                      :label="'Изображение'"
                      :multiple="false"
                      :file-type="$options.mediaTypesByKeys.IMAGE"
                      :value="fields.imageId"
                      @change="fields.imageId = $event"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" v-if="!!routes">
                    <span>Маршруты</span>
                    <v-select
                      v-model="fields.routes"
                      multiple
                      outlined
                      item-value="id"
                      item-text="title"
                      :items="routes"
                      :disabled="disabled"
                      :loading="loading"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" v-if="!!audioguides">
                    <span>Аудиогиды</span>
                    <v-select
                      v-model="fields.audioguides"
                      multiple
                      outlined
                      item-value="id"
                      item-text="title"
                      :items="audioguides"
                      :disabled="disabled"
                      :loading="loading"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col cols="12" v-if="!!cities">
                    <span>Города</span>
                    <v-select
                      outlined
                      item-value="id"
                      item-text="title"
                      :items="cities"
                      :disabled="disabled"
                      :loading="loading"
                      v-model="fields.cityId"
                    />
                  </v-col>
                </v-row>
                <v-col cols="12">
                  <SeoSettings
                    :data="data"
                    :loading="loading"
                    :disabled="disabled"
                    @on-input-seo="fields = Object.assign({}, fields, $event)"
                  />
                </v-col>
              </PageContent>
            </v-container>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="4">
        <ManageSidePanel
          ref="manageSidePanel"
          isShowSortOrder
          :is-new="isNew"
          :is-loading="loading"
          :language="language"
          :created-at="createtAt"
          :updated-at="updatedAt"
          :translations="fields.translations"
          :isSubmitDisabled="disabled"
          :entity-id="getDataId"
          :entity-type="'touristAgency'"
          :sortOrder="fields.sortOrder"
          @update-translations="Object.assign(fields.translations, $event)"
          @language-update="$emit('language-update', $event)"
          @cancel="$router.push({ name: 'page-tourist-agencies' })"
          @submit="submit"
          @delete="$emit('delete-tourist-agency')"
          @update-sort-order="fields.sortOrder = Number($event)"
        >
          <template #info-append>
            <StatusPicker
              align="center"
              cols="12"
              title="Статус:"
              :disabled="disabled"
              :loading="loading"
              :status="fields.status"
              @status:change="fields.status = $event"
            />
            <v-row v-if="!!fields.detailsEntityId" align="center">
              <v-col cols="6">
                <span>Посмотреть общий доступ</span>
              </v-col>
              <v-col cols="6">
                <v-btn
                  icon
                  link
                  color="primary"
                  :to="redirectToSharedAccess"
                  :loading="loading"
                  ><v-icon>mdi-account-group</v-icon></v-btn
                >
              </v-col>
            </v-row>
            <v-row v-if="!isNew">
              <v-col>
                <DetailComponent
                  :loading="loading"
                  :disabled="disabled"
                  :details-entity-id="fields.detailsEntityId"
                  :details-entity-type="fields.detailsEntityType"
                  @change:details-entity-type="changeDetailsEntityType($event)"
                  @change:details-entity-id="fields.detailsEntityId = $event"
                />
              </v-col>
            </v-row>
          </template>
          <template #body>
            <v-card v-if="getEvents?.length || getAudioguides?.length || getRoutes?.length" flat>
              <v-card-subtitle class="grey--text">Связи</v-card-subtitle>
              <v-card-text>
                <v-row>
                  <RelationComponent
                    :events="getEvents"
                    :audioguides="getAudioguides"
                    :routes="getRoutes"
                    :loading="loading"
                    :disabled="disabled"
                  />
                </v-row>
              </v-card-text>
            </v-card>
          </template>
        </ManageSidePanel>
      </v-col>
    </v-row>
  </form>
</template>
<script>
import { mixForm } from '@/utils/form.mix.js'
import validationTranslations from '@/mixins/translations/validationTranslations.mix.js'
import prepareTranslations from '@/mixins/translations/prepareTranslations.mix'

import validationRules from './validation-rules.js'
import { mediaTypesByKeys } from '@/configs/mediaFileTypes'

import FormAbstract from '@/components/abstract/form'
import ManageSidePanel from '@/components/manage-side-panel'
import MediaFilePicker from '@/components/media-file-picker'
import SeoSettings from '@/components/seo-settings'
import TranslationIcon from '@/components/translation-icon/translationIcon'
import QuillEditorVue from '@/components/quill-editor'
import FormHeader from '@/components/form-header'
import InputTitleSlug from '@/components/inputs/InputTitleSlug'
import StatusPicker from '@/components/status-picker'
import PageContent from '@/components/page-content/PageContent.vue'
import ProgressCircular from '@/components/progress-circular'
import RelationComponent from '@/components/relation-component'
import DetailComponent from '@/components/detail-component'

export default {
  extends: FormAbstract,
  mixins: [validationTranslations, prepareTranslations, mixForm],
  name: 'FormPageTouristAgency',
  validationRules,
  mediaTypesByKeys: mediaTypesByKeys(),
  components: {
    PageContent,
    InputTitleSlug,
    TranslationIcon,
    ManageSidePanel,
    MediaFilePicker,
    SeoSettings,
    FormHeader,
    QuillEditorVue,
    StatusPicker,
    ProgressCircular,
    RelationComponent,
    DetailComponent
  },
  props: {
    data: {
      type: Object,
      default: null
    },
    cities: {
      type: Array,
      default: null
    },
    routes: {
      type: Array,
      default: null
    },
    audioguides: {
      type: Array,
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    isNew: {
      type: Boolean,
      default: true
    },
    language: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      fields: {
        name: null,
        slug: undefined,
        status: null,
        description: null,
        shortDescription: null,
        imageId: null,
        routes: null,
        audioguides: null,
        cityId: null,
        seoMetaTitle: null,
        seoMetaDescription: null,
        seoDisallowIndex: false,
        seoMetaKeywords: null,
        seoRelCanonical: null,
        seoOgImageId: null,
        detailsEntityId: null,
        detailsEntityType: null,
        translations: { ru: { isEnabled: true } },
        sortOrder: null
      }
    }
  },
  computed: {
    getTitle () {
      if (this.data) {
        return this.data?.name || ''
      }
      return null
    },
    createtAt () {
      return this.data?.createtAt || null
    },
    updatedAt () {
      return this.data?.updatedAt || null
    },
    validationRulesVars () {
      return { id: this.data?.id, ctx: this, isNew: this.isNew }
    },
    getDataId () {
      return this?.data?.id || null
    },
    getAudioguides () {
      return this.data?.audioguides.map(audioguide => ({
        ...audioguide,
        __typename: 'Audioguide'
      }))
    },
    getEvents () {
      return this.data?.events || null
    },
    getRoutes () {
      return this.data?.routes.map(route => ({
        ...route,
        __typename: 'Route'
      }))
    }
  },
  watch: {
    data (data) {
      this.populate(data)
      this.fields.audioguides =
        this.data?.audioguides?.map(audioguide => audioguide.id) || null
      this.fields.routes = this.data?.routes?.map(route => route.id) || null
      this.fields.cityId = this.data?.city?.id || null
    }
  },
  methods: {
    async submit () {
      const isValid = await this.validate()
      if (!isValid) {
        this.mixScrollToError(this.$v)
        return
      }
      if (!this.mixIsValidTranslations(this.fields.translations)) {
        this.$errorMsg('Не выбран ни один язык для контента!')
        return
      }
      const payload = {
        ...this.composeFormData(this.fields)
      }
      if (!this.fields.detailsEntityId) {
        delete payload.detailsEntityId
        delete payload.detailsEntityType
      }
      this.mixPrepareTranslations(payload)
      this.$emit('submit', {
        data: payload
      })
    },
    changeDetailsEntityType (event) {
      this.fields.detailsEntityType = event
      this.fields.detailsEntityId = null
    }
  }
}
</script>
